import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { queryClient } from '@/lib/queryClient';
import { ApiError, authedJson } from '@/lib/http';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PassProductRow {
  id: string;
  name: string;
  description: string | null;
  creatorId: string;
  creatorName: string;
  venueId: string | null;
  venueName: string | null;
  sessionCount: number;
  priceCents: number;
  isActive: boolean;
  classes: Array<{ id: string; title: string; isArchived: boolean }>;
  sold: number;
  activeHolders: number;
  punchesRemaining: number;
}

interface ClassOption {
  id: string;
  title: string;
  creatorId: string;
  creatorName: string;
  venueId: string | null;
  venueName: string | null;
  isPublished: boolean;
  isArchived: boolean;
  passProductId: string | null;
  passProductName: string | null;
}

interface FormState {
  name: string;
  description: string;
  creatorId: string;
  venueId: string; // 'none' = not venue-scoped
  sessionCount: string;
  price: string; // dollars
  isActive: boolean;
  classIds: string[];
}

const NO_VENUE = 'none';
const usd = (c: number) => `$${(c / 100).toFixed(2)}`;

const emptyForm: FormState = {
  name: '',
  description: '',
  creatorId: '',
  venueId: NO_VENUE,
  sessionCount: '10',
  price: '',
  isActive: true,
  classIds: [],
};

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      return (JSON.parse(err.body) as { error?: string }).error ?? err.message;
    } catch {
      return err.body || err.message;
    }
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

export function PassProductsAdmin() {
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);

  const { data: productData, isLoading } = useQuery<{ products: PassProductRow[] }>({
    queryKey: ['/api/admin/pass-products'],
    queryFn: () => authedJson('/api/admin/pass-products'),
  });
  const { data: optionData } = useQuery<{ classes: ClassOption[] }>({
    queryKey: ['/api/admin/pass-products/class-options'],
    queryFn: () => authedJson('/api/admin/pass-products/class-options'),
  });
  const products = productData?.products ?? [];
  const classOptions = useMemo(() => optionData?.classes ?? [], [optionData]);

  const creators = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of classOptions) byId.set(c.creatorId, c.creatorName);
    return [...byId]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [classOptions]);

  // Venues where the chosen creator teaches — a venue pass can only cover
  // classes held there.
  const venues = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of classOptions) {
      if (c.creatorId === form.creatorId && c.venueId && c.venueName)
        byId.set(c.venueId, c.venueName);
    }
    return [...byId].map(([id, name]) => ({ id, name }));
  }, [classOptions, form.creatorId]);

  // Classes eligible for this pass: the payee creator's, at the venue if set.
  const eligibleClasses = useMemo(
    () =>
      classOptions.filter(
        c =>
          c.creatorId === form.creatorId &&
          (form.venueId === NO_VENUE || c.venueId === form.venueId) &&
          (!c.isArchived || form.classIds.includes(c.id))
      ),
    [classOptions, form.creatorId, form.venueId, form.classIds]
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        creatorId: form.creatorId,
        venueId: form.venueId === NO_VENUE ? null : form.venueId,
        sessionCount: parseInt(form.sessionCount, 10),
        priceCents: Math.round(parseFloat(form.price || '0') * 100),
        isActive: form.isActive,
        classIds: form.classIds,
      };
      return authedJson(
        editingId ? `/api/admin/pass-products/${editingId}` : '/api/admin/pass-products',
        {
          method: editingId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
    },
    onSuccess: () => {
      toast({ title: editingId ? 'Pass updated' : 'Pass created' });
      setDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/pass-products'] });
      void queryClient.invalidateQueries({
        queryKey: ['/api/admin/pass-products/class-options'],
      });
    },
    onError: err =>
      toast({
        title: 'Could not save pass',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p: PassProductRow) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      description: p.description ?? '',
      creatorId: p.creatorId,
      venueId: p.venueId ?? NO_VENUE,
      sessionCount: String(p.sessionCount),
      price: (p.priceCents / 100).toFixed(2),
      isActive: p.isActive,
      classIds: p.classes.map(c => c.id),
    });
    setDialogOpen(true);
  };

  const toggleClass = (id: string, checked: boolean) =>
    setForm(f => ({
      ...f,
      classIds: checked ? [...f.classIds, id] : f.classIds.filter(c => c !== id),
    }));

  // Classes being pulled in from another pass — shown so the admin knows the
  // move (and, if it empties that pass, its students' balances) will follow.
  const movingIn = eligibleClasses.filter(
    c => form.classIds.includes(c.id) && c.passProductId && c.passProductId !== editingId
  );

  const canSave =
    form.name.trim().length >= 3 &&
    !!form.creatorId &&
    parseInt(form.sessionCount, 10) >= 2 &&
    form.price !== '' &&
    !saveMutation.isPending;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle>Punch Passes</CardTitle>
          <CardDescription>
            One pass can cover several classes — e.g. every yoga class at a venue. Students can use
            a punch on any class the pass covers.
          </CardDescription>
        </div>
        <Button onClick={openCreate} data-testid="button-new-pass-product">
          New pass
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!isLoading && products.length === 0 && (
          <p className="text-sm text-muted-foreground">No punch passes yet.</p>
        )}
        {products.map(p => (
          <div
            key={p.id}
            className="rounded-lg border p-4 space-y-2"
            data-testid={`pass-product-${p.id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{p.name}</span>
                  <Badge variant={p.isActive ? 'default' : 'secondary'}>
                    {p.isActive ? 'On sale' : 'Not on sale'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {p.sessionCount} sessions · {usd(p.priceCents)} · paid to {p.creatorName}
                  {p.venueName ? ` · ${p.venueName}` : ''}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => openEdit(p)}>
                Edit
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {p.classes.length === 0 ? (
                <span className="text-sm text-muted-foreground">No classes</span>
              ) : (
                p.classes.map(c => (
                  <Badge key={c.id} variant="outline">
                    {c.title}
                    {c.isArchived ? ' (archived)' : ''}
                  </Badge>
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {p.sold} sold · {p.activeHolders} students with punches left · {p.punchesRemaining}{' '}
              punches outstanding
            </p>
          </div>
        ))}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit pass' : 'New pass'}</DialogTitle>
            <DialogDescription>
              Pick the classes this pass is good for. Sales are paid to the creator you choose, so a
              pass can only cover that creator&apos;s classes for now.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pass-name">Name</Label>
              <Input
                id="pass-name"
                placeholder="Fig Tree Yoga Pass"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pass-description">Description (optional)</Label>
              <Textarea
                id="pass-description"
                rows={2}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Paid to (creator)</Label>
                <Select
                  value={form.creatorId || undefined}
                  onValueChange={v =>
                    setForm(f => ({ ...f, creatorId: v, venueId: NO_VENUE, classIds: [] }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose creator" />
                  </SelectTrigger>
                  <SelectContent>
                    {creators.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Venue</Label>
                <Select
                  value={form.venueId}
                  onValueChange={v =>
                    setForm(f => ({
                      ...f,
                      venueId: v,
                      // Drop classes that aren't held at the newly chosen venue.
                      classIds:
                        v === NO_VENUE
                          ? f.classIds
                          : f.classIds.filter(
                              id => classOptions.find(c => c.id === id)?.venueId === v
                            ),
                    }))
                  }
                  disabled={!form.creatorId}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_VENUE}>Any venue</SelectItem>
                    {venues.map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pass-sessions">Sessions per pass</Label>
                <Input
                  id="pass-sessions"
                  type="number"
                  min={2}
                  max={100}
                  value={form.sessionCount}
                  onChange={e => setForm(f => ({ ...f, sessionCount: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pass-price">Price ($)</Label>
                <Input
                  id="pass-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.price}
                  onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label htmlFor="pass-active">On sale</Label>
                <p className="text-xs text-muted-foreground">
                  Turning this off stops new sales. Passes already sold keep working.
                </p>
              </div>
              <Switch
                id="pass-active"
                checked={form.isActive}
                onCheckedChange={v => setForm(f => ({ ...f, isActive: v }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Classes this pass covers</Label>
              {!form.creatorId ? (
                <p className="text-sm text-muted-foreground">Choose a creator first.</p>
              ) : eligibleClasses.length === 0 ? (
                <p className="text-sm text-muted-foreground">No classes match.</p>
              ) : (
                <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-2">
                  {eligibleClasses.map(c => (
                    <label
                      key={c.id}
                      className="flex cursor-pointer items-start gap-2 rounded p-1.5 hover:bg-muted"
                    >
                      <Checkbox
                        checked={form.classIds.includes(c.id)}
                        onCheckedChange={v => toggleClass(c.id, v === true)}
                        className="mt-0.5"
                      />
                      <span className="min-w-0 text-sm">
                        {c.title}
                        <span className="block text-xs text-muted-foreground">
                          {[
                            c.venueName ?? 'No venue',
                            c.isPublished ? null : 'unpublished',
                            c.passProductId && c.passProductId !== editingId
                              ? `now on "${c.passProductName}"`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {movingIn.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {movingIn.length === 1 ? 'This class moves' : 'These classes move'} off{' '}
                  {movingIn.length === 1 ? 'its' : 'their'} current pass. If that leaves a pass with
                  no classes, students&apos; existing punches on it move to this pass too.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => saveMutation.mutate()} disabled={!canSave}>
              {saveMutation.isPending ? 'Saving…' : 'Save pass'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
